import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Award, Download, Loader2, CheckCircle2, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { PageHero } from "../components/FormKit";

export const CertificateSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "checking" });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error" }); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/certificate/status/${sessionId}`);
        if (data.payment_status === "paid" && data.certificate) { setState({ status: "paid", cert: data.certificate }); return; }
        if (data.status === "expired") { setState({ status: "expired" }); return; }
      } catch {}
      if (attempts++ < 6) setTimeout(poll, 2000); else setState({ status: "pending" });
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="certificate-success-page">
      <PageHero eyebrow="TAVEN Academy" title="Your Certificate" />
      <section className="py-14 px-4 mx-auto max-w-2xl">
        {state.status === "checking" && <div className="text-center py-12"><Loader2 className="w-10 h-10 animate-spin mx-auto text-taven-gold" /><p className="mt-4 text-muted-foreground">Confirming your payment…</p></div>}
        {state.status === "paid" && <Certificate cert={state.cert} />}
        {(state.status === "pending") && <div className="text-center py-12"><p className="text-muted-foreground">Payment is processing. Please refresh in a moment.</p></div>}
        {(state.status === "error" || state.status === "expired") && <div className="text-center py-12"><p className="text-muted-foreground">We couldn't confirm this payment.</p><Link to="/education" className="text-taven-gold mt-3 inline-block">Back to Academy</Link></div>}
      </section>
    </div>
  );
};

export const Certificate = ({ cert }) => (
  <div>
    <div className="flex items-center gap-2 justify-center text-green-600 mb-6"><CheckCircle2 className="w-5 h-5" /> <span className="font-bold">Payment confirmed — certificate issued!</span></div>
    <div id="taven-certificate" className="relative border-4 border-taven-gold bg-white p-8 md:p-12 text-center" data-testid="certificate-display">
      <div className="border border-black/10 p-8">
        <p className="text-xs font-bold uppercase tracking-[0.4em] text-taven-gold">TAVEN SYSTEM LLC</p>
        <Award className="w-14 h-14 text-taven-gold mx-auto my-4" />
        <p className="text-sm uppercase tracking-widest text-muted-foreground">Certificate of Completion</p>
        <p className="text-xs text-muted-foreground mt-6">This certifies that</p>
        <h2 className="text-3xl font-black font-display mt-1">{cert.student_name}</h2>
        <p className="text-xs text-muted-foreground mt-4">has successfully completed</p>
        <h3 className="text-xl font-black font-display text-taven-orange mt-1">{cert.course_title}</h3>
        <div className="flex items-center justify-center gap-8 mt-8 text-xs">
          <div><p className="text-muted-foreground">Date</p><p className="font-bold">{new Date(cert.issued_date).toLocaleDateString()}</p></div>
          <div><p className="text-muted-foreground">Certificate ID</p><p className="font-bold">{cert.certificate_id}</p></div>
        </div>
        <p className="text-[10px] uppercase tracking-[0.3em] text-taven-gold mt-8">Build. Earn. Grow. Leave a Legacy.</p>
      </div>
    </div>
    <div className="flex flex-wrap gap-3 justify-center mt-6">
      <button onClick={() => window.print()} data-testid="cert-download" className="bg-taven-black text-white px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-taven-orange transition-colors flex items-center gap-2"><Download className="w-4 h-4" /> Download / Print</button>
      <Link to={`/verify/${cert.certificate_id}`} className="border border-black px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-black hover:text-white transition-colors flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Verify Certificate</Link>
    </div>
  </div>
);

export default CertificateSuccess;
